import {
  Box,
  Grid,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'
import React, { useEffect } from 'react'
import { useQuery } from 'react-query'

import { Barcode } from 'src/components/Barcode'
import {
  Truckload,
  TruckloadSMContainer,
  TruckloadSMInventory,
  TruckloadSMShipment,
} from 'src/api/models'
import { skuit } from 'src/services/skuit'
import useProfileContext from 'src/context/ProfileContext'

const useStyles = makeStyles(() => ({
  table: {
    width: '100%',
    margin: '0',
    padding: '0',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '.25em',
    fontSize: '.85em',
    fontWeight: 'bold',
  },
  tr: {
    borderTop: '1px solid gray',
    borderBottom: '1px solid gray',
    padding: '.25em',
    fontSize: '.8em',
  },
  td: {
    borderTop: '1px solid gray',
    borderBottom: '1px solid gray',
    padding: '.25em',
  },
  strong: {
    fontWeight: 'bold',
  },
  label: {
    maxWidth: '90%',
    margin: 'auto',
    border: '1px solid gray',
    borderRadius: '1rem',
    padding: '0.5rem',
  },
  tspaced: {
    marginTop: '1rem',
  },
  bspaced: {
    marginBottom: '1rem',
  },
}))

interface InventorySMProps {
  inventory: TruckloadSMInventory[]
}

const InventorySM = (props: InventorySMProps) => {
  const { inventory } = props
  const classes = useStyles()

  return (
    <>
      <table className={classes.table} aria-label="units table">
        <thead>
          <tr>
            <th className={classes.th}>
              Sku
            </th>
            <th className={classes.th}>
              Product
            </th>
            <th className={classes.th}>
              Number of Units
            </th>
            <th className={classes.th}>
              Total in Shipment
            </th>
            <th className={classes.th}>
              Ordered
            </th>
          </tr>
        </thead>
        <tbody>
          {inventory.map((row) => (
            <tr
              key={row.sku?.guid}
              className={`card-break ${classes.tr}`}
            >
              <td className={classes.td} align="right">{row.sku?.sku}</td>
              <td className={classes.td} align="right">{row.sku?.product?.name}</td>
              <td className={classes.td} align="right">{row.numUnits}</td>
              <td className={classes.td} align="right">{row.totalUnits}</td>
              <td className={classes.td} align="right">{row.numOrdered}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

interface ContainerSMProps {
  container: TruckloadSMContainer
}

const ContainerSM = (props: ContainerSMProps) => {
  const { container } = props

  return (
    <>
      <Typography variant="h5">
        Name: {container.name}
      </Typography>
      <Typography variant="h5">
        SquID: {container.guid}
      </Typography>
      <InventorySM inventory={container.inventory || []} />
    </>
  )
}

interface ShipmentSMProps {
  shipment: TruckloadSMShipment
}

const ShipmentSM = (props: ShipmentSMProps) => {
  const { shipment } = props
  const barcode = { barcode: shipment.guid, type: 'datamatrix', displayName: '' }
  const classes = useStyles()

  return (
    <div className={`${classes.label} ${classes.bspaced}`}>
      <div className="card-break">
        <Box px={2} pt={2}>
          <Grid
            container
            justify="space-between"
            alignItems="center"
            spacing={2}
          >
            <Grid item xs={10}>
              <Box>
                <Typography variant="h3">Shipment</Typography>
              </Box>
              <Box>
                <Typography variant="h5">SquID: {shipment.guid}</Typography>
              </Box>
              <Box>
                <Typography variant="h5">Tracking #: {shipment.trackingNumber || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="h5">
                  {shipment.order ? `Order #${shipment.order.number || '-'}` : 'No order on shipment' }
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={2}>
              <Barcode barcode={barcode} />
            </Grid>
          </Grid>
        </Box>
      </div>

      <div className={`${classes.bspaced} ${classes.tspaced}`}>
        <Typography variant="h3">
          Inventory by Pallet
        </Typography>
      </div>
      {!!shipment.containers?.length && shipment.containers.map((container: TruckloadSMContainer) => (
        <ContainerSM container={container} key={container.guid} />
      ))}
      {!!shipment.inventory?.length && (
        <>
          <Typography variant="h5">
            No Pallet
          </Typography>
          <InventorySM inventory={shipment.inventory || []} />
        </>
      )}
    </div>
  )
}

interface Props {
  truckload: Truckload
  onReadyToPrint?: () => void
}

export const TruckloadShippingManifest = (props: Props) => {
  const { truckload, onReadyToPrint } = props
  const barcode = { barcode: truckload.guid, type: 'datamatrix', displayName: '' }
  const classes = useStyles()

  const profileContext = useProfileContext()


  const { data, isLoading } = useQuery(
    ['shipping-manifest', truckload.guid],
    () => skuit.sc.freight.truckloads.shippingManifest(truckload?.guid as string)
  )

  useEffect(() => {
    if (!isLoading) {
      onReadyToPrint?.()
    }
  }, [onReadyToPrint, isLoading])

  if (isLoading) {
    return <div>Truckload Shipping Manifest not ready to print</div>
  }

  return (
    <div className={`always-page-break ${classes.bspaced}`}>
      <Grid
        container
        justify="space-between"
        alignItems="center"
        spacing={2}
        className={`${classes.label} ${classes.bspaced}`}
      >
        <Grid item xs={3}>
          <Barcode barcode={barcode} />
        </Grid>
        <Grid item xs={9}>
          <Box>
            <Typography variant="h3">Truckload</Typography>
          </Box>
          <Box>
            <Typography variant="h5">SquID: {truckload.guid}</Typography>
          </Box>
          <Box>
            <Typography variant="h5">Tracking #: {truckload.trackingNumber || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="h5">Bill of Lading #: {truckload.billOfLadingNumber || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="h5">Trailer #: {truckload.trailerNumber || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption">Printed on {moment().format('lll')} by {profileContext.profile?.firstName} {profileContext.profile?.lastName}</Typography>
          </Box>
        </Grid>
      </Grid>
      {data.shipments.map((shipment: TruckloadSMShipment) => (
        <ShipmentSM key={shipment.guid} shipment={shipment} />
      ))}
    </div>
  )
}
